import { useCallback, useEffect } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import {
  emailAccountService,
  CreateEmailAccountData,
  EmailAccount,
  SenderHealth
} from '@/lib/services/email-accounts';
import { dfyService, ProvisioningStep } from '@/lib/services/dfy';

export type CombinedEmailAccount = {
  id: string;
  email: string;
  displayName: string;
  status: 'ready' | 'pending' | 'provisioning' | 'failed';
  isDfy: boolean;
  personaFirstName?: string;
  personaLastName?: string;
  createdAt: string;
  emails_sent?: number;
  is_warming?: boolean;
  warmup_emails?: number;
  health_score?: number;
  /** Whether the warmup ramp applies to this account. Owner-controlled. */
  warmup_throttle?: boolean;
  /** Ramp state, enforced by falcon-mailer and shown here. Null when throttling is off. */
  warmup?: {
    sendingDay: number;
    ramping: boolean;
    dailyCap: number | null;
    fullSpeedOn: string | null;
  } | null;
  /** Seven-day sending health, computed server-side. */
  health?: SenderHealth;
  // Original account data for operations
  originalAccountId?: string;
  dfyAccountId?: string;
  provisioningSteps?: ProvisioningStep[];
};

const fetcher = async (): Promise<CombinedEmailAccount[]> => {
  const [regularAccounts, dfyResponse] = await Promise.all([
    fetch('/api/email-accounts', { credentials: 'include' }).then(res => res.json()) as Promise<EmailAccount[]>,
    dfyService.listAccounts(),
  ]);

  // Convert regular accounts
  const regularCombined: CombinedEmailAccount[] = (regularAccounts || []).map(account => ({
    id: account.account_id,
    email: account.email,
    displayName: account.display_name,
    status: 'ready' as const,
    isDfy: false,
    createdAt: account.created_at,
    emails_sent: account.emails_sent || 0,
    is_warming: account.is_warming || false,
    warmup_emails: account.warmup_emails || 0,
    health_score: account.health_score,
    warmup_throttle: account.warmup_throttle ?? true,
    warmup: account.warmup,
    health: account.health,
    originalAccountId: account.account_id,
  }));

  // Only include DFY accounts that are NOT yet linked to a regular account
  // (once linked, they show up in regularAccounts instead)
  const dfyCombined: CombinedEmailAccount[] = dfyResponse.accounts
    .filter(account => !account.emailAccountId)
    .map(account => ({
      id: account.id,
      email: account.emailAddress,
      displayName: `${account.personaFirstName} ${account.personaLastName}`,
      status: mapDfyStatus(account.status),
      isDfy: true,
      personaFirstName: account.personaFirstName,
      personaLastName: account.personaLastName,
      createdAt: account.createdAt,
      dfyAccountId: account.id,
      provisioningSteps: account.provisioningSteps,
    }));

  // Combine and sort by creation date (newest first)
  const combined = [...regularCombined, ...dfyCombined].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  // Deduplicate by email, keeping the most recent (first after sort)
  const seen = new Set<string>();
  return combined.filter(account => {
    if (seen.has(account.email)) return false;
    seen.add(account.email);
    return true;
  });
};

function mapDfyStatus(status: string): CombinedEmailAccount['status'] {
  switch (status) {
    case 'ready':
      return 'ready';
    case 'failed':
      return 'failed';
    case 'pending':
      return 'pending';
    default:
      return 'provisioning';
  }
}

export function useEmailAccounts() {
  const { data: emailAccounts, mutate, isLoading, error } = useSWR<CombinedEmailAccount[]>(
    'combined-email-accounts',
    fetcher
  );

  // Poll for updates while any accounts are provisioning
  const hasProvisioningAccounts = emailAccounts?.some(
    account => account.status === 'pending' || account.status === 'provisioning'
  );

  useEffect(() => {
    if (!hasProvisioningAccounts) return;

    const interval = setInterval(() => {
      mutate();
    }, 5000);

    return () => clearInterval(interval);
  }, [hasProvisioningAccounts, mutate]);

  const createEmailAccount = useCallback(async (data: CreateEmailAccountData) => {
    try {
      const newAccount = await emailAccountService.createEmailAccount(data);
      mutate();
      toast.success('Email account added successfully');
      return newAccount;
    } catch (error) {
      console.error('Failed to create email account:', error);
      toast.error('Failed to create email account');
      throw error;
    }
  }, [mutate]);

  const updateEmailAccount = useCallback(async (id: string, displayName: string) => {
    const res = await fetch(`/api/email-accounts/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName }),
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      throw new Error(detail.error || 'Failed to update email account');
    }
    await mutate();
  }, [mutate]);

  const deleteEmailAccount = useCallback(async (id: string) => {
    try {
      await emailAccountService.deleteEmailAccount(id);
      mutate(emailAccounts?.filter(account => account.originalAccountId !== id), false);
      toast.success('Email account deleted successfully');
    } catch (error) {
      console.error('Failed to delete email account:', error);
      toast.error('Failed to delete email account');
      throw error;
    }
  }, [emailAccounts, mutate]);

  // The warmup ramp, which throttles cold sends from an address new to cold
  // sending. Distinct from the legacy enable/disable warmup calls below, which
  // drive the mailer's own warmup feature.
  const setWarmupThrottle = useCallback(async (accountId: string, enabled: boolean) => {
    try {
      const res = await fetch(`/api/email-accounts/${accountId}/warmup-throttle`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.error || 'Failed to update warmup throttling');
      }
      await mutate();
      toast.success(enabled ? 'Warmup throttling on' : 'Warmup throttling off');
    } catch (error) {
      console.error('Failed to update warmup throttling:', error);
      toast.error('Failed to update warmup throttling');
      throw error;
    }
  }, [mutate]);

  const enableWarmup = useCallback(async (accountId: string) => {
    try {
      await emailAccountService.enableWarmup(accountId);
      mutate();
      toast.success('Warmup enabled');
    } catch (error) {
      console.error('Failed to enable warmup:', error);
      toast.error('Failed to enable warmup');
      throw error;
    }
  }, [mutate]);

  const disableWarmup = useCallback(async (accountId: string) => {
    try {
      await emailAccountService.disableWarmup(accountId);
      mutate();
      toast.success('Warmup disabled');
    } catch (error) {
      console.error('Failed to disable warmup:', error);
      toast.error('Failed to disable warmup');
      throw error;
    }
  }, [mutate]);

  return {
    emailAccounts: emailAccounts || [],
    isLoading,
    error,
    mutate,
    createEmailAccount,
    updateEmailAccount,
    deleteEmailAccount,
    setWarmupThrottle,
    enableWarmup,
    disableWarmup,
    hasProvisioningAccounts,
  };
}
